import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const DREAMER_DRAFT_ID_PARAM = "dreamerDraftId";

/**
 * Detects a `?dreamerDraftId=<draftId>` query param (the Slack email-draft card's
 * "Open in Dreamhub" link), opens the Dreamer drawer, and removes the param so
 * it doesn't persist in history.
 *
 * Returns the draft id the chat list should bring into view, and a callback
 * to clear it once handled.
 */
export function useDreamerDraftDeepLink(
  setChatDrawerOpen: (open: boolean) => void
): [string | null, () => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusDraftId, setFocusDraftId] = useState<string | null>(null);

  useEffect(() => {
    if (!searchParams.has(DREAMER_DRAFT_ID_PARAM)) return;
    const draftId = searchParams.get(DREAMER_DRAFT_ID_PARAM)?.trim();
    if (draftId) setFocusDraftId(draftId);
    setChatDrawerOpen(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(DREAMER_DRAFT_ID_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams, setChatDrawerOpen]);

  const clearFocusDraftId = useCallback(() => setFocusDraftId(null), []);

  return [focusDraftId, clearFocusDraftId];
}
